import { useMemo, useState } from 'react';
import { Alert, Card, Col, Empty, Input, List, Row, Segmented, Space, Spin, Tag, Typography } from 'antd';
import { BookOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { helpGuideService } from '@/services/helpGuide.service';
import type { HelpGuideListItem } from '@/types/help-guide';
import { useOptionalAiAgent } from '@/contexts/AiAgentContext';

const { Title, Text, Paragraph } = Typography;

const KIND_LABEL: Record<HelpGuideListItem['kind'], string> = {
  task: 'Task',
  workflow: 'Workflow',
  concept: 'Concept',
  troubleshoot: 'Troubleshooting',
};

/** Help Center — searchable how-to guides for the modules installed in the current company. */
export default function HelpCenterPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const module = params.get('module') ?? '';
  const [draft, setDraft] = useState(q);
  const ai = useOptionalAiAgent();

  const { data, isLoading, error } = useQuery({
    queryKey: ['help-guides', q, module],
    queryFn: () => helpGuideService.list({ q: q || undefined, module: module || undefined }),
    staleTime: 5 * 60_000,
  });

  const allModules = useQuery({
    queryKey: ['help-guides', '', ''],
    queryFn: () => helpGuideService.list(),
    staleTime: 5 * 60_000,
  });

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: HelpGuideListItem[] }>();
    for (const g of data?.guides ?? []) {
      const entry = map.get(g.module) ?? { label: g.moduleLabel, items: [] };
      entry.items.push(g);
      map.set(g.module, entry);
    }
    return [...map.entries()];
  }, [data]);

  const setParam = (key: 'q' | 'module', value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const moduleOptions = [
    { label: 'All', value: '' },
    ...(allModules.data?.modules ?? []).map((m) => ({ label: `${m.label} (${m.count})`, value: m.key })),
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <BookOutlined /> Help Center
        </Title>
        <Text type="secondary">
          Step-by-step guides for this ERP, tailored to your permissions and your company setup.
        </Text>
      </Space>

      <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 16 }}>
        <Input.Search
          allowClear
          size="large"
          placeholder='Search, e.g. "book a purchase order" or "post a GRN"'
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onSearch={(v) => setParam('q', v.trim())}
        />
        {moduleOptions.length > 1 ? (
          <div style={{ overflowX: 'auto' }}>
            <Segmented options={moduleOptions} value={module} onChange={(v) => setParam('module', String(v))} />
          </div>
        ) : null}
        {ai ? (
          <Text type="secondary">
            Can&apos;t find it?{' '}
            <a
              onClick={() => ai.askAssistant(q ? `How do I ${q}?` : 'How do I use this ERP?')}
              role="button"
            >
              <QuestionCircleOutlined /> Ask the ERP Assistant
            </a>
          </Text>
        ) : null}
      </Space>

      {error ? (
        <Alert type="error" showIcon message={error instanceof Error ? error.message : 'Could not load guides'} />
      ) : null}

      <Spin spinning={isLoading}>
        {!isLoading && grouped.length === 0 ? (
          <Empty description={q ? `No guides match "${q}"` : 'No guides available yet'} />
        ) : null}
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {grouped.map(([key, group]) => (
            <Card key={key} size="small" title={`${group.label} (${group.items.length})`}>
              <Row gutter={[16, 0]}>
                {[0, 1].map((col) => (
                  <Col key={col} xs={24} md={12}>
                    <List
                      size="small"
                      dataSource={group.items.filter((_, i) => i % 2 === col)}
                      renderItem={(g) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <Space size={6} wrap>
                                <Link to={`/help/${encodeURIComponent(g.id)}`}>{g.title}</Link>
                                {g.kind !== 'task' ? <Tag>{KIND_LABEL[g.kind]}</Tag> : null}
                              </Space>
                            }
                            description={
                              <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
                                {g.summary}
                              </Paragraph>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </Col>
                ))}
              </Row>
            </Card>
          ))}
        </Space>
      </Spin>
    </div>
  );
}
